import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { UserAPIService } from '../../../../extensions/services/api/user-api.service';
import { Subscription } from 'rxjs';
import { scrollTop } from 'src/app/extensions/utilities';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RequestListFilterComponent } from 'src/app/features/my-requests/request-list/request-list-filter/request-list-filter.component';
import { isEqual } from 'lodash';
import { CLIENT_DATA_WRITE_RIGHTS } from 'src/app/extensions/constants/right-constants';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { DictionaryRequestStatus } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';

@Component({
  selector: 'app-client-data-list',
  templateUrl: './client-data-list.component.html',
  styleUrls: ['./client-data-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class ClientDataListComponent implements OnInit, OnDestroy {
  requests: app.models.GetClientDataRequest[] = [];
  requestsProps: app.models.DynamicPropsDictionary;
  public hasData = true;
  public hasFilterData = true;

  private pageIndex = 1;
  private maxIndex: number;
  public pageFilter: app.models.GetClientDataRequestsModelPageFilter;
  public initialFilterState: app.models.GetRequestsModelPageFilter;
  public activeFilterCount = 0;

  userRights: string[];
  readOnly: boolean = true;

  public subscriptions: Subscription[] = [];
  public showBackToTop: boolean = false;
  private modalRequestsStatuses: app.models.DictionaryDataModel[];
  public isFiltered: boolean = false;

  constructor(
    private _router: Router,
    private _requestService: RequestAPIService,
    private _scrollService: ScrollService,
    private _ngbModal: NgbModal,
    private _userAPIService: UserAPIService,
    private _dictionaryAPIService: DictionaryAPIService
  ) {}

  ngOnInit(): void {
    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;
    this.initialFilterState = structuredClone(this.pageFilter);

    this._userAPIService.getUserRights().subscribe(result => {
      this.userRights = result.rights.map(x => x.code);
      this.readOnly = !this.userRights.some(userRight => CLIENT_DATA_WRITE_RIGHTS.includes(userRight));
    });

    this._dictionaryAPIService.getDictionaryRequestStatuses().subscribe(requestStatuses => {
      this.modalRequestsStatuses = requestStatuses.data;

      this.pageFilter.statusesIds = this.modalRequestsStatuses
        .filter(status => status.code !== DictionaryRequestStatus.CLOSED)
        .map(status => status.id);

      this.initialFilterState = structuredClone(this.pageFilter);
      this.activeFilterCount = this.pageFilter.statusesIds.length;
      this.loadData();
    });

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let subscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._requestService.getClientDataRequests(this.pageIndex, this.pageFilter).subscribe(result => {
          this.requests = [...this.requests, ...result.data.values];

          this.requests = this.requests.map(request => {
            if (request.dictionaryClientDataRequestType.code === 'CLNT_REQ_BANK_ACC' && request.otherObservations)
              return request;

            const parts = request.otherObservations.split(';');
            const iban = parts.length > 0 ? parts[0] : '-';
            const bank = parts.length > 1 ? parts[1] : '-';

            return {
              ...request,
              iban,
              bank,
            };
          });

          this.maxIndex = result.data.totalPages;

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData(filterMode: boolean = false) {
    this._requestService.getClientDataRequests(this.pageIndex, this.pageFilter).subscribe(result => {
      this.requests = result.data.values;

      this.requests = this.requests.map(request => {
        if (request.dictionaryClientDataRequestType.code !== 'CLNT_REQ_BANK_ACC' || !request.otherObservations)
          return request;

        const parts = request.otherObservations.split(';');
        const iban = parts[0];
        const bank = parts.length > 1 ? parts[1] : null;

        return {
          ...request,
          iban,
          bank,
        };
      });

      this.requestsProps = result.props;
      this.maxIndex = result.data.totalPages;
      this.isFiltered = filterMode;
      if (filterMode) {
        this.hasFilterData = this.requests.length > 0;
      } else {
        this.hasData = this.requests.length > 0;
      }

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });
  }

  public filterHandler() {
    let modal = this._ngbModal.open(RequestListFilterComponent, { backdrop: 'static' });
    modal.componentInstance.modalFilters = this.pageFilter;
    modal.componentInstance.initialFilterState = this.initialFilterState;
    modal.componentInstance.modalRequestsStatuses = this.modalRequestsStatuses;

    modal.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          statusesIds: this.pageFilter.statusesIds,
        };
        this.activeFilterCount = result;
        this.pageIndex = 1;
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }

        this.loadData(true);
      }
    });
  }

  public resetFiltersHandler() {
    this.activeFilterCount = 0;

    this.initialFilterState = {
      ...this.initialFilterState,
      statusesIds: this.pageFilter.statusesIds,
    };

    if (!isEqual(this.pageFilter, this.initialFilterState)) {
      this.pageFilter = structuredClone(this.initialFilterState);
    }

    this.pageFilter = {
      statusesIds: [],
    } as app.models.GetRequestsModelPageFilter;

    this.pageIndex = 1;
    this.hasFilterData = true;
    this.loadData();
  }

  viewDetailsHandler = (selectedRequest: string) => {
    this._router.navigate(['requests/client-data', selectedRequest]);
  };

  createNewRequest() {
    this._router.navigate(['requests/client-data/new']);
  }

  redirectRequestTypeHandler() {
    this._router.navigate(['requests/entry']);
  }

  scrollToTop(): void {
    scrollTop();
  }
}
